import { useState, useEffect, useCallback, useRef } from "react";
import { API_BASE_URL } from "../../../../constants";

/**
 * Custom hook for fetching and managing user data
 * @param {Object} options - Configuration options for the hook
 * @param {Object} options.defaultFilters - Default filters to apply
 * @param {Object} options.authToken - Auth token for API requests
 * @param {Function} options.onLogout - Callback to handle logout when auth expires
 * @param {Function} options.getAuthHeaders - Function to get updated auth headers
 * @returns {Object} User data, loading state, filters, and methods to interact with the data
 */
const useUserData = ({
  defaultFilters = {},
  authToken,
  onLogout,
  getAuthHeaders,
}) => {
  // State for data and UI
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);
  const [hasMoreData, setHasMoreData] = useState(false);

  // State for filters and search
  const [filters, setFilters] = useState(defaultFilters);
  const [columnFilters, setColumnFilters] = useState({});
  const [activeFilterColumn, setActiveFilterColumn] = useState(null);

  // Data cache ref
  const dataCache = useRef({});
  const currentQueryKey = useRef("");

  // Generate a cache key based on filters
  const generateCacheKey = useCallback(
    (filterObj, colFilters, lastKey = null) => {
      const filtersKey = JSON.stringify(filterObj || {});
      const columnFiltersKey = JSON.stringify(colFilters || {});
      const lastKeyString = lastKey ? JSON.stringify(lastKey) : "";
      return `${filtersKey}|${columnFiltersKey}|${lastKeyString}`;
    },
    []
  );

  // Load users from the API
  const loadUsers = useCallback(
    async (resetPagination = true) => {
      try {
        setIsLoading(true);
        setError(null);

        const headers = getAuthHeaders();

        // Always apply default sorting by UserId descending
        const currentFilters = { ...filters };
        currentFilters.sortBy = "userId";
        currentFilters.sortOrder = "desc";

        // Build query parameters from filters
        const queryParams = new URLSearchParams();
        queryParams.append("type", "getAllUsers");

        if (currentFilters.daysBack) {
          queryParams.append("daysBack", currentFilters.daysBack);
        }

        if (currentFilters.sex) {
          queryParams.append("sex", currentFilters.sex);
        }

        if (currentFilters.hasGroupMeId !== undefined) {
          queryParams.append("hasGroupMeId", currentFilters.hasGroupMeId);
        }

        if (currentFilters.startDate) {
          queryParams.append("startDate", currentFilters.startDate);
        }

        if (currentFilters.endDate) {
          queryParams.append("endDate", currentFilters.endDate);
        }

        if (currentFilters.sortBy) {
          queryParams.append("sortBy", currentFilters.sortBy);
        }

        if (currentFilters.sortOrder) {
          queryParams.append("sortOrder", currentFilters.sortOrder);
        }

        // Only include lastEvaluatedKey if we're not resetting pagination
        // and we have a stored key from previous requests
        if (!resetPagination && lastEvaluatedKey) {
          queryParams.append(
            "lastEvaluatedKey",
            JSON.stringify(lastEvaluatedKey)
          );
        }

        // Generate cache key for this specific request
        const newQueryKey = generateCacheKey(
          filters,
          columnFilters,
          resetPagination ? null : lastEvaluatedKey
        );
        currentQueryKey.current = newQueryKey;

        // Check if we have cached data for this request
        if (dataCache.current[newQueryKey]) {
          const cachedData = dataCache.current[newQueryKey];
          if (!resetPagination) {
            // For pagination, append new results to existing data
            setUsers((prevUsers) => [...prevUsers, ...cachedData.users]);
          } else {
            // For new requests, replace data
            setUsers(cachedData.users);
          }
          setLastEvaluatedKey(cachedData.lastEvaluatedKey);
          setHasMoreData(cachedData.hasMoreData);
          setIsLoading(false);
          return;
        }

        const response = await fetch(
          `${API_BASE_URL}?${queryParams.toString()}`,
          {
            method: "GET",
            headers,
          }
        );

        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            if (onLogout) {
              onLogout();
            }
            throw new Error(
              "Session expired or unauthorized, please login again"
            );
          }
          throw new Error(`Error: ${response.status}`);
        }

        const data = await response.json();

        // Process the data and apply any column filters
        let processedUsers = data.users || [];

        // Apply column filters (client-side filtering)
        if (Object.keys(columnFilters).length > 0) {
          processedUsers = processedUsers.filter((user) => {
            return Object.entries(columnFilters).every(([column, value]) => {
              if (value === "all") return true;
              if (value === "empty") {
                return (
                  !user[column] || user[column] === "" || user[column] === "N/A"
                );
              }

              // Special case for fields that might be arrays or comma-separated strings
              if (
                column === "reInterestedInEvents" ||
                column === "selectedEvents"
              ) {
                const fieldValue = user[column];
                if (typeof fieldValue === "string") {
                  return fieldValue.includes(value);
                }
                if (Array.isArray(fieldValue)) {
                  return fieldValue.includes(value);
                }
                return false;
              }

              // Handle name column which is a combination of firstName and lastName
              if (column === "name") {
                const fullName = `${user.firstName || ""} ${
                  user.lastName || ""
                }`.toLowerCase();
                return fullName.includes(value.toLowerCase());
              }

              // Standard string comparison for other fields
              return (
                String(user[column] || "").toLowerCase() === value.toLowerCase()
              );
            });
          });
        }

        // Sort the data
        processedUsers.sort((a, b) => {
          const userIdA = String(a.userId || "");
          const userIdB = String(b.userId || "");
          return userIdB.localeCompare(userIdA); // Descending order
        });

        // Store in cache
        dataCache.current[newQueryKey] = {
          users: processedUsers,
          lastEvaluatedKey: data.lastEvaluatedKey || null,
          hasMoreData: !!data.lastEvaluatedKey,
        };

        if (!resetPagination) {
          // For pagination, append new results to existing data
          setUsers((prevUsers) => [...prevUsers, ...processedUsers]);
        } else {
          // For new requests, replace data
          setUsers(processedUsers);
        }

        setLastEvaluatedKey(data.lastEvaluatedKey || null);
        setHasMoreData(!!data.lastEvaluatedKey);
      } catch (error) {
        console.error("Error loading users:", error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    },
    [
      filters,
      columnFilters,
      lastEvaluatedKey,
      getAuthHeaders,
      onLogout,
      generateCacheKey,
    ]
  );

  // Apply filters and load data
  const applyFilters = useCallback(() => {
    setLastEvaluatedKey(null);
    loadUsers(true);
  }, [loadUsers]);

  // Load more data (pagination)
  const loadMore = useCallback(() => {
    if (hasMoreData && !isLoading) {
      loadUsers(false);
    }
  }, [hasMoreData, isLoading, loadUsers]);

  // Handle filter changes
  const handleFilterChange = useCallback((name, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [name]: value,
    }));
  }, []);

  // Clear all filters
  const clearFilters = useCallback(() => {
    setFilters(defaultFilters);
    setColumnFilters({});
    setLastEvaluatedKey(null);
  }, [defaultFilters]);

  // Handle column filter changes
  const handleColumnFilterChange = useCallback((column, value) => {
    setColumnFilters((prev) => {
      const newFilters = { ...prev };

      // If 'all' is selected, remove the filter
      if (value === "all") {
        delete newFilters[column];
      } else {
        newFilters[column] = value;
      }

      return newFilters;
    });

    // Auto close the filter dropdown after selection
    setActiveFilterColumn(null);
  }, []);

  // Toggle filter dropdown visibility
  const toggleFilterDropdown = useCallback((column) => {
    setActiveFilterColumn((prev) => (prev === column ? null : column));
  }, []);

  // Initial data load
  useEffect(() => {
    if (authToken) {
      loadUsers();
    }
  }, [authToken, loadUsers]);

  // Return all data and methods
  return {
    users,
    isLoading,
    error,
    hasMoreData,
    filters,
    setFilters,
    applyFilters,
    handleFilterChange,
    loadMore,
    clearFilters,
    columnFilters,
    handleColumnFilterChange,
    activeFilterColumn,
    toggleFilterDropdown,
    refresh: () => loadUsers(true),
  };
};

export default useUserData;
